import { inject, Injectable } from '@angular/core'

import {
  auditTime,
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  map,
  merge,
  Observable,
  of,
  skip,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs'
import { scan, shareReplay } from 'rxjs/operators'

import {
  BookmarkType,
  DeviceSensorsConfig,
  Driver,
  DriverAssignHistoryReportData,
  EngineHoursSourceTypes,
  Group,
  OdometerSourceTypes,
  TelemetryReadMode,
  Vehicle,
} from '@ti/contracts'
import { ApiService, TelemetryReadModeContext } from '@ti/core/api'
import type { LegacyTelemetryBinding } from '@ti/core/api/scopes/fleet/routes/vehicles.route'
import { Profile } from '@ti/core/auth/services'
import { RangeMs } from '@ti/core/common/contracts'
import { LOGIN_AS_SESSION, LOGIN_AS_SESSION_STORAGE_KEY } from '@ti/core/common/contracts/login-as'
import { Memoize } from '@ti/core/common/decorators'
import { BrowserSessionStorage } from '@ti/core/common/storage/session.storage'
import { injectDestroy$ } from '@ti/core/common/utils'
import { AUTH_SESSION, UserAttributes } from '@ti/core/contracts/auth-session'
import { BOOKMARKS_FACADE } from '@ti/core/contracts/bookmarks'
import { UIVehicleAggregate, VehicleTelemetry } from '@ti/core/contracts/vehicle'
import { DriversStore } from '@ti/shared/drivers/services/drivers.store'
import { GroupsStore } from '@ti/shared/groups/services/groups.store'
import { LocationsIndex } from '@ti/shared/locations/services/locations-index'

import { CustomSensorsProvider } from './custom-sensors.provider'
import { FlespiClient } from './flespi.client'
import { HistoricalDriverProvider } from './historical-driver.provider'
import { HistoricalTelemetryProvider } from './historical-telemetry.provider'
import { VehicleMaintenanceStatusProvider } from './maintenance-status.provider'
import { TelemetryProvider } from './telemetry.provider'
import { VehiclesStore } from './vehicles.store'

export type FleetPresentationMode =
  | { type: 'realtime' }
  | { type: 'historical'; currentTime: number; totalRange: RangeMs; vehicleIds: string[] }

export type HistoricalTelemetryEntryIssue =
  | { type: 'unsupported-comparison'; mode: TelemetryReadMode }
  | { type: 'historical-map-unavailable'; mode: TelemetryReadMode }
  | { type: 'persistence-unavailable' }

interface LegacyHistoricalRun {
  entryGeneration: number
  scopeEpoch: number
  scopeKey: string
  mode: 'legacy'
  bindingKey?: string
}

interface GeofenceMatch {
  latitude: number
  longitude: number
  locationsVersion: number
  locationType: 'address' | 'geofence'
  geofenceNames: string[]
  // The provider's telemetry object and its enriched copy, reused while the source is unchanged
  source: VehicleTelemetry
  enriched: VehicleTelemetry
}

interface AggregateCacheEntry {
  key: string
  telemetry: VehicleTelemetry | undefined
  value: UIVehicleAggregate
}

// TODO: Rename to FleetVehiclesProvider
@Injectable({ providedIn: 'root' })
export class FleetStore {
  protected readonly api = inject(ApiService)
  protected readonly driversStore = inject(DriversStore)
  protected readonly groupsStore = inject(GroupsStore)
  protected readonly profile = inject(Profile)
  protected readonly authSession = inject(AUTH_SESSION, { optional: true })
  protected readonly loginAsSession = inject(LOGIN_AS_SESSION, { optional: true })
  protected readonly sessionStorage = inject(BrowserSessionStorage, { optional: true })
  protected readonly locations = inject(LocationsIndex)
  protected readonly telemetry = inject(TelemetryProvider)
  protected readonly telemetryReadModeContext = inject(TelemetryReadModeContext)
  protected readonly vehicleStore = inject(VehiclesStore)
  protected readonly customSensors = inject(CustomSensorsProvider)
  protected readonly flespiClient = inject(FlespiClient)
  protected readonly maintenanceStatuses = inject(VehicleMaintenanceStatusProvider)
  protected readonly bookmarks = inject(BOOKMARKS_FACADE)
  protected readonly bookmarkItems$ = this.bookmarks.items$
  protected readonly destroy$ = injectDestroy$()

  public readonly isLoading$ = this.vehicleStore.state$.isLoading

  // Caches the last produced aggregate per vehicle id so the object reference
  // is only replaced when its key fields or the telemetry object change.
  protected aggregateCache = new Map<string, AggregateCacheEntry>()

  // Last geofence match per device, scoped by telemetry source - the real-time and
  // historical providers hold distinct telemetry objects for the same device.
  protected readonly geofenceMatchCache: Record<
    FleetPresentationMode['type'],
    Map<number, GeofenceMatch>
  > = {
    realtime: new Map(),
    historical: new Map(),
  }

  /** Current telemetry mode signal; starts in real-time. */
  private readonly presentationMode$ = new BehaviorSubject<FleetPresentationMode>({
    type: 'realtime',
  })
  private readonly historicalTelemetryIssueSubject =
    new BehaviorSubject<HistoricalTelemetryEntryIssue | null>(null)
  public readonly historicalTelemetryIssue$ = this.historicalTelemetryIssueSubject.asObservable()

  private historicalModeEntryGeneration = 0
  private scopeIdentitySnapshotReady = false
  private scopeIdentityKey?: string
  private scopeIdentityEpoch = 0
  private scopeAttributes?: UserAttributes | null
  private activeLegacyHistoricalRun?: LegacyHistoricalRun
  private activeLegacyBindingSubscription?: Subscription

  private historicalDriverProvider?: HistoricalDriverProvider
  private historicalTelemetryProvider?: HistoricalTelemetryProvider

  public constructor() {
    this.authSession?.state.currentUserAttributes$
      .pipe(takeUntil(this.destroy$))
      .subscribe((attributes) => this.observeAuthScope(attributes))

    Promise.resolve()
      .then(() => this.initialize())
      .catch((e) => console.error('Cannot initialize FleetStore', e))

    this.bookmarks.revalidateIfStale().catch((e) => console.error('Cannot load bookmarks', e))

    this.destroy$.subscribe(() => {
      this.historicalModeEntryGeneration++
      this.clearHistoricalProviders()
      this.presentationMode$.complete()
      this.historicalTelemetryIssueSubject.complete()
    })
  }

  public getPresentationMode() {
    return this.presentationMode$.getValue()
  }

  /**
   * Switches to historical mode: loads telemetry for the given point-in-time
   * and total range, updating `vehicles$` after each loaded page.
   *
   * @param vehicleIds   List of vehicle IDs to load telemetry for.
   * @param currentTime  The specific moment (ms) to evaluate telemetry state at.
   * @param totalRange   Full date range [startMs, endMs] to load messages for.
   */
  public async enterHistoricalMode(
    vehicleIds: string[],
    currentTime: number,
    totalRange: RangeMs,
  ): Promise<void> {
    const entryGeneration = ++this.historicalModeEntryGeneration
    this.historicalTelemetryIssueSubject.next(null)

    let telemetryReadMode: TelemetryReadMode | undefined
    try {
      telemetryReadMode = this.telemetryReadModeContext.capture()
    } catch {
      this.returnToRealtimeWithHistoricalIssue({ type: 'persistence-unavailable' })
      return
    }

    if (telemetryReadMode) {
      if (telemetryReadMode === 'legacy') {
        await this.enterLegacyHistoricalMode(vehicleIds, currentTime, totalRange, entryGeneration)
        return
      }

      this.returnToRealtimeWithHistoricalIssue({
        type: 'unsupported-comparison',
        mode: telemetryReadMode,
      })
      return
    }

    // TODO: Check before creating a driver provider (plan, shared-link)
    this.clearHistoricalProviders()
    this.historicalDriverProvider = new HistoricalDriverProvider(this.api)

    this.historicalTelemetryProvider = new HistoricalTelemetryProvider(
      this.flespiClient,
      this.customSensors,
    )

    const vehicles = await firstValueFrom(this.vehicleStore.state$.items).then((vehicles) => {
      return vehicleIds?.length ? vehicles.filter((v) => vehicleIds.includes(v.id)) : vehicles
    })

    if (entryGeneration !== this.historicalModeEntryGeneration) return

    // Start loading asynchronously; state$ will emit after each chunk
    const historicalTelemetryProvider = this.historicalTelemetryProvider
    const historicalDriverProvider = this.historicalDriverProvider
    if (!historicalTelemetryProvider) return
    historicalTelemetryProvider
      .initialize(vehicles, currentTime, totalRange)
      .catch((e) => console.error('FleetStore: historical telemetry load failed', e))

    historicalDriverProvider?.setVehicles(vehicleIds)
    historicalDriverProvider?.setCurrentTime(currentTime)
    historicalDriverProvider?.setSelectedRange(totalRange)

    this.presentationMode$.next({ type: 'historical', currentTime, totalRange, vehicleIds })
  }

  /**
   * Scrubs the currently active historical view to a new point in time, without re-fetching
   * telemetry. No-op if historical mode isn't active.
   */
  public setHistoricalCurrentTime(currentTime: number): void {
    if (!this.historicalTelemetryProvider) return

    this.historicalDriverProvider?.setCurrentTime(currentTime)
    this.historicalTelemetryProvider.setCurrentTime(currentTime)

    const mode = this.presentationMode$.getValue()
    if (mode.type === 'historical') {
      this.presentationMode$.next({ ...mode, currentTime })
    }
  }

  /**
   * Returns to real-time telemetry mode, tearing down the historical provider.
   */
  public exitHistoricalMode(): void {
    this.historicalModeEntryGeneration++
    this.presentationMode$.next({ type: 'realtime' })
    this.historicalTelemetryIssueSubject.next(null)
    this.clearHistoricalProviders()
  }

  private returnToRealtimeWithHistoricalIssue(issue: HistoricalTelemetryEntryIssue): void {
    this.historicalModeEntryGeneration++
    this.presentationMode$.next({ type: 'realtime' })
    this.clearHistoricalProviders()
    // Diagnostic only: the map simply falls back to realtime, with no new message for the user.
    console.debug('Historical telemetry unavailable, showing realtime', issue)
    this.historicalTelemetryIssueSubject.next(issue)
  }

  private async enterLegacyHistoricalMode(
    vehicleIds: string[],
    currentTime: number,
    totalRange: RangeMs,
    entryGeneration: number,
  ): Promise<void> {
    vehicleIds = [...vehicleIds]
    totalRange = [...totalRange]
    this.presentationMode$.next({ type: 'realtime' })
    this.clearHistoricalProviders()
    if (this.scopeIdentitySnapshotReady) this.observeAuthScope(this.scopeAttributes)

    if (!this.scopeIdentitySnapshotReady || !this.scopeIdentityKey) {
      this.returnToRealtimeWithHistoricalIssue({
        type: 'historical-map-unavailable',
        mode: 'legacy',
      })
      return
    }

    const run: LegacyHistoricalRun = {
      entryGeneration,
      scopeEpoch: this.scopeIdentityEpoch,
      scopeKey: this.scopeIdentityKey,
      mode: 'legacy',
    }
    this.activeLegacyHistoricalRun = run

    let allVehicles: Vehicle[]
    try {
      allVehicles = await firstValueFrom(this.vehicleStore.state$.items)
    } catch {
      this.failLegacyRun(run, { type: 'historical-map-unavailable', mode: 'legacy' })
      return
    }

    if (!this.isLegacyRunCurrent(run)) return

    const selectedVehicles = selectHistoricalVehicles(allVehicles, vehicleIds)
    const bindings = selectedVehicles?.map(snapshotLegacyTelemetryBinding)
    if (
      !selectedVehicles?.length ||
      !bindings ||
      bindings.some((binding) => binding === undefined) ||
      selectedVehicles.length > 128
    ) {
      this.failLegacyRun(run, { type: 'historical-map-unavailable', mode: 'legacy' })
      return
    }

    const frozenBindings = bindings as LegacyTelemetryBinding[]
    run.bindingKey = legacyBindingKey(frozenBindings)
    const frozenVehicles = selectedVehicles.map((vehicle, index) => {
      const binding = frozenBindings[index]
      return {
        ...vehicle,
        primaryExternalDeviceId: binding.externalDeviceId,
        primaryDevice: vehicle.primaryDevice
          ? { ...vehicle.primaryDevice, sensorsConfig: binding.sensorsConfig }
          : vehicle.primaryDevice,
      }
    })

    this.watchLegacyVehicleBindings(run, vehicleIds)
    if (!this.isLegacyRunCurrent(run)) return

    this.historicalDriverProvider = new HistoricalDriverProvider(this.api)
    const driverProvider = this.historicalDriverProvider
    this.historicalTelemetryProvider = new HistoricalTelemetryProvider(
      this.flespiClient,
      this.customSensors,
    )

    const telemetryProvider = this.historicalTelemetryProvider
    telemetryProvider
      .initialize(frozenVehicles, currentTime, totalRange, {
        telemetryReadMode: 'legacy',
        isCurrent: () => this.isLegacyRunCurrent(run),
      })
      .catch(() => {
        this.failLegacyRun(run, { type: 'historical-map-unavailable', mode: 'legacy' })
      })

    driverProvider.setVehicles(vehicleIds)
    driverProvider.setCurrentTime(currentTime)
    driverProvider.setSelectedRange(totalRange)
    this.presentationMode$.next({ type: 'historical', currentTime, totalRange, vehicleIds })
  }

  private observeAuthScope(attributes: UserAttributes | null | undefined): void {
    this.scopeAttributes = attributes
    const nextKey = this.authScopeKey(attributes)
    if (!this.scopeIdentitySnapshotReady) {
      this.scopeIdentitySnapshotReady = true
      this.scopeIdentityKey = nextKey
      this.scopeIdentityEpoch++
      return
    }

    if (nextKey === this.scopeIdentityKey) return
    this.scopeIdentityKey = nextKey
    this.scopeIdentityEpoch++

    const activeRun = this.activeLegacyHistoricalRun
    if (activeRun) {
      this.failLegacyRun(activeRun, { type: 'historical-map-unavailable', mode: 'legacy' })
    }
  }

  private authScopeKey(attributes: UserAttributes | null | undefined): string | undefined {
    if (
      !attributes?.sub ||
      !Number.isFinite(attributes.tspId) ||
      !Number.isFinite(attributes.fleetId) ||
      !Number.isFinite(attributes.role)
    ) {
      return undefined
    }

    let storedLoginAs: unknown = null
    if (this.sessionStorage) {
      try {
        const stored = this.sessionStorage.getItem(LOGIN_AS_SESSION_STORAGE_KEY)
        if (stored !== null) {
          const parsed = JSON.parse(stored)
          if (
            !parsed ||
            typeof parsed.sessionId !== 'string' ||
            typeof parsed.targetUserId !== 'string'
          )
            return undefined
          storedLoginAs = [parsed.sessionId, parsed.targetUserId]
        }
      } catch {
        return undefined
      }
    }
    return JSON.stringify([
      attributes.sub,
      attributes.tspId,
      attributes.fleetId,
      attributes.role,
      this.loginAsSession?.sessionId ?? null,
      this.loginAsSession?.targetUserId ?? null,
      storedLoginAs,
    ])
  }

  private isLegacyRunCurrent(run: LegacyHistoricalRun): boolean {
    if (
      this.activeLegacyHistoricalRun !== run ||
      run.entryGeneration !== this.historicalModeEntryGeneration
    ) {
      return false
    }

    if (
      this.scopeIdentityEpoch !== run.scopeEpoch ||
      this.scopeIdentityKey !== run.scopeKey ||
      this.authScopeKey(this.scopeAttributes) !== run.scopeKey
    ) {
      this.failLegacyRun(run, { type: 'historical-map-unavailable', mode: 'legacy' })
      return false
    }

    let currentMode: TelemetryReadMode | undefined
    try {
      currentMode = this.telemetryReadModeContext.capture()
    } catch {
      this.failLegacyRun(run, { type: 'persistence-unavailable' })
      return false
    }
    if (currentMode !== 'legacy') {
      this.failLegacyRun(
        run,
        currentMode === 'new'
          ? { type: 'unsupported-comparison', mode: 'new' }
          : { type: 'historical-map-unavailable', mode: 'legacy' },
      )
      return false
    }

    return true
  }

  private watchLegacyVehicleBindings(run: LegacyHistoricalRun, vehicleIds: string[]): void {
    this.activeLegacyBindingSubscription?.unsubscribe()
    const subscription = this.vehicleStore.state$.items
      .pipe(takeUntil(this.destroy$))
      .subscribe((vehicles) => {
        if (!this.isLegacyRunCurrent(run)) return
        const selected = selectHistoricalVehicles(vehicles, vehicleIds)
        const bindings = selected?.map(snapshotLegacyTelemetryBinding)
        if (
          !bindings ||
          bindings.some((binding) => binding === undefined) ||
          legacyBindingKey(bindings as LegacyTelemetryBinding[]) !== run.bindingKey
        ) {
          this.failLegacyRun(run, { type: 'historical-map-unavailable', mode: 'legacy' })
        }
      })
    if (this.activeLegacyHistoricalRun === run) this.activeLegacyBindingSubscription = subscription
    else subscription.unsubscribe()
  }

  private failLegacyRun(run: LegacyHistoricalRun, issue: HistoricalTelemetryEntryIssue): void {
    if (
      this.activeLegacyHistoricalRun !== run ||
      run.entryGeneration !== this.historicalModeEntryGeneration
    ) {
      return
    }
    this.returnToRealtimeWithHistoricalIssue(issue)
  }

  private clearHistoricalProviders(): void {
    this.activeLegacyBindingSubscription?.unsubscribe()
    this.activeLegacyBindingSubscription = undefined
    this.activeLegacyHistoricalRun = undefined
    this.historicalDriverProvider?.destroy()
    this.historicalDriverProvider = undefined
    this.historicalTelemetryProvider?.destroy()
    this.historicalTelemetryProvider = undefined
  }

  @Memoize()
  public get isHistoricalMode$() {
    return this.presentationMode$.pipe(map((t) => t.type === 'historical'))
  }

  @Memoize()
  public get vehicles$(): Observable<UIVehicleAggregate[]> {
    // Map the index refresh signal to a monotonic version so consumers can
    // cheaply detect whether the location index changed since last emission.
    const locationsVersion$ = this.locations.refreshed$.pipe(scan((version) => version + 1, 0))

    // Resolve the active telemetry map based on the current mode
    const emptyTelemetryMap = of(new Map<number, VehicleTelemetry>())
    const activeTelemetryByDevice$: Observable<Map<number, VehicleTelemetry>> =
      this.presentationMode$.pipe(
        switchMap((mode) => {
          if (mode.type === 'realtime') {
            return this.telemetry.telemetry$
          }
          // Historical mode: stream the historical provider's state as a telemetry map
          if (!this.historicalTelemetryProvider) return emptyTelemetryMap
          return this.historicalTelemetryProvider.state$.pipe(map((s) => s.telemetryByDevice))
        }),
      )

    // Per-vehicle driver-link assignment active at the historical currentTime, resolved by the
    // historical driver provider. Empty when the provider isn't available or in real-time mode.
    const historicalAssignments$: Observable<Map<string, DriverAssignHistoryReportData>> =
      this.isHistoricalMode$.pipe(
        switchMap((isHistoricalMode) => {
          return isHistoricalMode && this.historicalDriverProvider
            ? this.historicalDriverProvider.state$.driverAssignmentByVehicle
            : of(new Map<string, DriverAssignHistoryReportData>())
        }),
      )

    const matchGeofenceNames$ = this.profile.state.pipe(
      map((profile) => !!profile.fleet?.matchGeofenceNames),
      distinctUntilChanged(),
    )
    const driversMap$ = this.driversStore.state$.items.pipe(
      map((drivers) => new Map(drivers.map((d) => [d.id, d]))),
    )
    const groupsMap$ = this.groupsStore.state$.items.pipe(
      map((groups) => new Map(groups.map((g) => [g.id, g]))),
    )

    // Shared - the two branches below must see one set of emissions, from one subscription.
    const sources$ = combineLatest({
      allVehicles: this.vehicleStore.state$.items,
      driverMap: driversMap$,
      groupsMap: groupsMap$,
      telemetryByDevice: activeTelemetryByDevice$,
      matchGeofenceNames: matchGeofenceNames$,
      locationsVersion: locationsVersion$,
      telemetryMode: this.presentationMode$,
      historicalAssignments: historicalAssignments$,
      maintenanceStatuses: this.maintenanceStatuses.state$.statuses,
      bookmarkItems: this.bookmarkItems$,
    }).pipe(shareReplay({ bufferSize: 1, refCount: true }))

    // Aggregate rebuilds are expensive, so live telemetry is coalesced to one per window.
    const throttled$ = sources$.pipe(auditTime(256))

    // ...but entering/scrubbing/leaving historical mode swaps the telemetry source outright,
    // where a delayed rebuild reads as lag - those emissions skip the audit.
    // The new telemetry rides along: `telemetryByDevice` is combined ahead of `telemetryMode`,
    // so its switchMap has already re-subscribed (both providers replay) when the mode lands.
    const presentationModeChanged$ = sources$.pipe(
      distinctUntilChanged((prev, next) => prev.telemetryMode === next.telemetryMode),
      skip(1), // Initial mode - the throttled branch already carries it
    )

    return merge(throttled$, presentationModeChanged$).pipe(
      // Same tuple from both branches - drops the audit's echo of a bypassed emission.
      distinctUntilChanged(),
      map(
        ({
          allVehicles,
          driverMap,
          groupsMap,
          telemetryByDevice,
          matchGeofenceNames,
          locationsVersion,
          telemetryMode,
          historicalAssignments,
          maintenanceStatuses,
          bookmarkItems,
        }) => {
          if (!allVehicles?.length) {
            this.aggregateCache.clear()
            this.geofenceMatchCache.realtime.clear()
            this.geofenceMatchCache.historical.clear()
            return []
          }

          // Once per emission, not per vehicle. Equivalent to `bookmarks.hasBookmark` for every
          // reachable state: the only way this set is non-empty is a directory load, and every
          // load path is gated by the manager's feature/permission checks.
          const bookmarkedVehicleIds = new Set(
            bookmarkItems
              .filter((bookmark) => bookmark.type === BookmarkType.VehicleDetails)
              .map((bookmark) => bookmark.entityId),
          )

          // Vehicles currently viewed in historical mode; their driver is resolved from the
          // historical assignment snapshot rather than the live vehicle.driverId.
          const historicalVehicleIds =
            telemetryMode.type === 'historical' ? new Set(telemetryMode.vehicleIds) : undefined

          // Rebuild the cache each emission so references for removed vehicles are dropped.
          const nextCache = new Map<string, AggregateCacheEntry>()

          const result = allVehicles.map((vehicle) => {
            const deviceTelemetry = telemetryByDevice.get(vehicle.primaryExternalDeviceId)
            const t = matchGeofenceNames
              ? this.enrichTelemetryWithGeofence(
                  deviceTelemetry,
                  locationsVersion,
                  telemetryMode.type,
                )
              : deviceTelemetry

            const driverData = this.resolveDriverData(
              vehicle,
              driverMap,
              historicalVehicleIds?.has(vehicle.id) ? historicalAssignments : undefined,
            )
            const groupsData = this.resolveGroups(vehicle.groupIds, groupsMap)
            const driverGroups = this.resolveGroups(driverData?.groupIds, groupsMap)

            return this.buildAggregate(vehicle, t, nextCache, {
              driverData,
              groupsData,
              driverGroups,
              maintenanceStatus: maintenanceStatuses[vehicle.id]?.join('\n') ?? '',
              isBookmarked: bookmarkedVehicleIds.has(vehicle.id),
            })
          })

          this.aggregateCache = nextCache
          return result
        },
      ),
      takeUntil(this.destroy$),
      shareReplay({ bufferSize: 1, refCount: false }),
    )
  }

  // Resolves the driver record to expose on a vehicle aggregate.
  // - Real-time vehicles use their live `driverId` against the drivers map.
  // - Historical vehicles use the driver-link assignment active at the scrubbed time: the
  //   freshest driver record from the map when available, otherwise a minimal { id, name }
  //   fallback built from the link-history data itself.
  protected resolveDriverData(
    vehicle: Vehicle,
    driverMap: Map<string, Driver>,
    historicalAssignments: Map<string, DriverAssignHistoryReportData> | undefined,
  ): Driver | undefined {
    if (historicalAssignments) {
      const assignment = historicalAssignments.get(vehicle.id)
      if (!assignment) return undefined

      return (
        driverMap.get(assignment.driverId) ??
        ({ id: assignment.driverId, name: assignment.driverName } as Driver)
      )
    }

    return vehicle.driverId ? driverMap.get(vehicle.driverId) : undefined
  }

  // Resolves group ids into their group records, dropping ids missing from the map (e.g. a
  // deleted group). Used to pre-populate both `groupsData` (vehicle's groups) and
  // `driverGroups` (driver's groups) so consuming components don't need to filter groups themselves.
  protected resolveGroups(groupIds: string[] | undefined, groupsMap: Map<string, Group>): Group[] {
    if (!groupIds?.length) return []

    return groupIds.reduce<Group[]>((acc, id) => {
      const group = groupsMap.get(id)
      if (group) acc.push(group)
      return acc
    }, [])
  }

  // Returns the telemetry entry with its geofence/address location resolved, as a copy: the
  // provider's object is never mutated. The copy is reused while the provider object, position
  // and locations are unchanged, so the aggregate keeps its reference. Unchanged positions skip
  // the match but still re-apply it: every flespi message resets `locationType` to 'address'.
  protected enrichTelemetryWithGeofence(
    telemetry: VehicleTelemetry | undefined,
    locationsVersion: number,
    source: FleetPresentationMode['type'],
  ): VehicleTelemetry | undefined {
    if (!telemetry?.latitude || !telemetry?.longitude) return telemetry

    const cache = this.geofenceMatchCache[source]
    const cached = cache.get(telemetry.deviceId)
    if (
      cached &&
      cached.latitude === telemetry.latitude &&
      cached.longitude === telemetry.longitude &&
      cached.locationsVersion === locationsVersion
    ) {
      if (cached.source !== telemetry) {
        cached.source = telemetry
        cached.enriched = {
          ...telemetry,
          locationType: cached.locationType,
          geofenceNames: cached.geofenceNames,
        }
      }
      return cached.enriched
    }

    const locations = this.locations.match([telemetry.latitude, telemetry.longitude])
    const locationType = locations?.length ? 'geofence' : 'address'
    const geofenceNames = locations?.length ? locations.map((location) => location.name) : []
    const match: GeofenceMatch = {
      latitude: telemetry.latitude,
      longitude: telemetry.longitude,
      locationsVersion,
      locationType,
      geofenceNames,
      source: telemetry,
      enriched: { ...telemetry, locationType, geofenceNames },
    }

    cache.set(telemetry.deviceId, match)
    return match.enriched
  }

  // Produces the UI aggregate for a vehicle, reusing the previously emitted
  // reference while its key fields and the telemetry object are unchanged.
  protected buildAggregate(
    vehicle: Vehicle,
    telemetry: VehicleTelemetry | undefined,
    nextCache: Map<string, AggregateCacheEntry>,
    derived: {
      driverData: Driver | undefined
      groupsData: Group[]
      driverGroups: Group[]
      maintenanceStatus: string
      isBookmarked: boolean
    },
  ): UIVehicleAggregate {
    const { driverData, groupsData, driverGroups, maintenanceStatus, isBookmarked } = derived

    // Only the fields below influence the produced reference. Groups are identified by
    // id + name (a rename should still invalidate the reference).
    const key = [
      driverData?.id ?? '',
      driverData?.name ?? '',
      driverData?.photo ?? '',
      vehicle.updatedAt?.valueOf?.() ?? vehicle.updatedAt,
      telemetry?.lastReportedAt ?? '',
      // TODO: Figure out better way to handle telemetry updates
      telemetry?.isConnected,
      telemetry?.inSleepMode,
      telemetry?.lastStatusInvalid,
      groupsData.map((g) => `${g.id}:${g.name}`).join(','),
      driverGroups.map((g) => `${g.id}:${g.name}`).join(','),
      maintenanceStatus,
      isBookmarked,
    ].join('|')

    const cached = this.aggregateCache.get(vehicle.id)
    if (cached && cached.key === key && cached.telemetry === telemetry) {
      nextCache.set(vehicle.id, cached)
      return cached.value
    }

    const value = {
      ...vehicle,
      telemetry: telemetry ?? {},
      driverData,
      groupsData,
      driverGroups,
      maintenanceStatus,
      isBookmarked,
      bookmark: {
        name: vehicle.name,
        entityId: vehicle.id,
        route: `fleet/vehicles/${vehicle.id}`,
        type: BookmarkType.VehicleDetails,
      },
    } as UIVehicleAggregate

    nextCache.set(vehicle.id, { key, telemetry, value })
    return value
  }

  public async getById(id: string): Promise<UIVehicleAggregate | undefined>
  public async getById(id: string[]): Promise<UIVehicleAggregate[]>
  public async getById(
    id: string | string[],
  ): Promise<UIVehicleAggregate | UIVehicleAggregate[] | undefined> {
    const vehicles = await firstValueFrom(this.vehicles$)
    return Array.isArray(id)
      ? vehicles.filter((vehicle) => id.includes(vehicle.id))
      : vehicles.find((vehicle) => vehicle.id === id)
  }

  public refresh(ids?: string[]) {
    if (ids?.length) {
      this.vehicleStore.refreshById(ids)
    } else {
      this.vehicleStore.refreshAll()
    }
  }

  protected async initialize() {
    await this.vehicleStore.ensureIsFresh()
    await this.telemetry.initialize(this.vehicleStore.state.items())
  }
}

function selectHistoricalVehicles(
  vehicles: Vehicle[],
  vehicleIds: string[],
): Vehicle[] | undefined {
  const requestedIds = [...new Set(vehicleIds)]
  if (requestedIds.length !== vehicleIds.length) return undefined

  const selected = requestedIds.length
    ? vehicles.filter((vehicle) => requestedIds.includes(vehicle.id))
    : vehicles
  if (
    !selected.length ||
    (requestedIds.length > 0 && selected.length !== requestedIds.length) ||
    new Set(selected.map((vehicle) => vehicle.id)).size !== selected.length
  ) {
    return undefined
  }
  return selected
}

function snapshotLegacyTelemetryBinding(vehicle: Vehicle): LegacyTelemetryBinding | undefined {
  const externalDeviceId = vehicle.primaryExternalDeviceId
  const sensorsConfig = vehicle.primaryDevice?.sensorsConfig
  if (
    !vehicle.id ||
    !Number.isSafeInteger(externalDeviceId) ||
    externalDeviceId <= 0 ||
    sensorsConfig?.odometer_source_type !== OdometerSourceTypes.telemetry ||
    sensorsConfig?.engine_hours_source_type !== EngineHoursSourceTypes.telemetry
  ) {
    return undefined
  }

  try {
    const serializedConfig = JSON.stringify(sensorsConfig)
    if (typeof serializedConfig !== 'string') return undefined
    return {
      vehicleId: vehicle.id,
      externalDeviceId,
      sensorsConfig: JSON.parse(serializedConfig) as DeviceSensorsConfig,
    }
  } catch {
    return undefined
  }
}

function legacyBindingKey(bindings: LegacyTelemetryBinding[]): string {
  return stableJson(
    [...bindings]
      .sort((left, right) => left.vehicleId.localeCompare(right.vehicleId))
      .map((binding) => ({
        vehicleId: binding.vehicleId,
        externalDeviceId: binding.externalDeviceId,
        sensorsConfig: binding.sensorsConfig,
      })),
  )
}

function stableJson(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    const serialized = JSON.stringify(value)
    return typeof serialized === 'string' ? serialized : 'null'
  }
  if (Array.isArray(value)) return `[${value.map((item) => stableJson(item)).join(',')}]`
  const record = value as Record<string, unknown>
  return `{${Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableJson(record[key])}`)
    .join(',')}}`
}
